import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Task } from "./useTasks";

export const useCandidateTasks = (candidateId: string | undefined) => {
  return useQuery({
    queryKey: ['tasks', 'candidate', candidateId],
    queryFn: async (): Promise<Task[]> => {
      if (!candidateId) return [];

      const { data, error } = await supabase
        .from('tasks')
        .select('*')
        .contains('relations', { candidates: [candidateId] })
        .order('created_at', { ascending: false });

      if (error) {
        throw new Error(error.message);
      }

      // If we have tasks with assignees, fetch the assignee information separately
      if (data && data.length > 0) {
        const assigneeIds = data
          .filter(task => task.assigned_to)
          .map(task => task.assigned_to)
          .filter((id, index, arr) => arr.indexOf(id) === index); // Remove duplicates

        if (assigneeIds.length > 0) {
          try {
            const { data: assigneeData, error: assigneeError } = await supabase
              .rpc('get_multiple_users_display_info', {
                p_user_ids: assigneeIds
              });

            if (!assigneeError && assigneeData) {
              // Create a map of user_id to user info
              const assigneeMap = new Map(
                assigneeData.map(user => [user.user_id, user])
              );

              // Attach assignee info to tasks
              return data.map(task => ({
                ...task,
                assignee: task.assigned_to ? assigneeMap.get(task.assigned_to) : undefined
              }));
            }
          } catch (assigneeError) {
            console.warn('Failed to fetch assignee information:', assigneeError);
          }
        }
      }

      return data || [];
    },
    enabled: !!candidateId,
  });
};
